/* eslint-disable max-lines */
import { PDFDocument, rgb, StandardFonts, PDFFont } from 'pdf-lib';
import moment from 'moment';
import { NC4FormData } from '../views/pages/NewHire/FormNC4Modal';

const drawTextOnPage = (
  page: any,
  text: string | undefined | null,
  startX: number,
  y: number,
  font: PDFFont,
  size: number,
  color = rgb(0, 0, 0),
  characterSpacing: number = 0,
) => {
  if (text === undefined || text === null || String(text).trim() === '') return;

  let currentX = startX;
  const textToDraw = String(text);

  for (let i = 0; i < textToDraw.length; i++) {
    const char = textToDraw[i];
    page.drawText(char, {
      x: currentX,
      y: y,
      font,
      size,
      color,
    });
    const charWidth = font.widthOfTextAtSize(char, size);
    currentX += charWidth + characterSpacing;
  }
};

const drawCheckboxMarkOnPage = (
  page: any,
  x: number,
  y: number,
  font: PDFFont,
  size = 10,
  color = rgb(0, 0, 0),
  mark = 'X',
) => {
  page.drawText(mark, {
    x: x + 1.5,
    y: y + 1,
    font,
    size,
    color,
  });
};

const drawRightAlignedTextOnPage = (
  page: any,
  text: string | undefined | null,
  rightX: number,
  y: number,
  font: PDFFont,
  size: number,
  color = rgb(0, 0, 0),
  characterSpacing: number = 0,
) => {
  if (text === undefined || text === null || String(text).trim() === '') return;
  const textToDraw = String(text);
  let textWidth = 0;
  for (let i = 0; i < textToDraw.length; i++) {
    const char = textToDraw[i];
    textWidth += font.widthOfTextAtSize(char, size);
    if (i < textToDraw.length - 1) {
      textWidth += characterSpacing;
    }
  }
  const startX = rightX - textWidth;
  drawTextOnPage(page, textToDraw, startX, y, font, size, color, characterSpacing);
};

const bufferToBase64 = (bufferData: { type: string; data: number[] }) => {
  const uint8Array = new Uint8Array(bufferData.data);
  return new TextDecoder().decode(uint8Array);
};

export async function fillNC4Form(pdfUrl: string, formData: NC4FormData): Promise<Uint8Array | null> {
  try {
    const existingPdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];
    const secondPage = pages[1];
    const thirdPage = pages[2];
    const fourthPage = pages[3];

    const fontSize = 10;
    const black = rgb(2 / 255, 29 / 255, 149 / 255);
    const charSpacingDefault = 4;
    const charSpacingSSN = 8;
    const charSpacingZip = 5;
    const charSpacingName = 4;
    const charSpacingAddress = 5;

    // --- PAGE 1 ---
    drawRightAlignedTextOnPage(
      firstPage,
      formData.totalAllowances,
      527,
      221,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    if (formData.additionalAmount && parseFloat(formData.additionalAmount) >= 0) {
      const additionalAmountFormatted = parseFloat(formData.additionalAmount).toFixed(0);
      drawRightAlignedTextOnPage(
        firstPage,
        additionalAmountFormatted,
        527,
        202,
        font,
        fontSize,
        black,
        charSpacingDefault,
      );
    }

    if (formData.socialSecurityNumber) {
      let ssnCleaned = String(formData.socialSecurityNumber).replace(/\D/g, '');
      if (ssnCleaned.length > 9) ssnCleaned = ssnCleaned.substring(0, 9);
      ssnCleaned = ssnCleaned.padStart(9, '0');
      drawTextOnPage(firstPage, ssnCleaned.substring(0, 3), 62, 172, font, fontSize, black, charSpacingSSN);
      drawTextOnPage(firstPage, ssnCleaned.substring(3, 5), 115, 172, font, fontSize, black, charSpacingSSN);
      drawTextOnPage(firstPage, ssnCleaned.substring(5, 9), 153, 172, font, fontSize, black, charSpacingSSN);
    }

    if (formData.filingStatus === 'single_married_separately')
      drawCheckboxMarkOnPage(firstPage, 214, 174, font, 10, black);
    if (formData.filingStatus === 'head_of_household') drawCheckboxMarkOnPage(firstPage, 333, 174, font, 10, black);
    if (formData.filingStatus === 'married_jointly_surviving_spouse')
      drawCheckboxMarkOnPage(firstPage, 409, 174, font, 10, black);

    drawTextOnPage(firstPage, formData.firstName?.toUpperCase(), 62, 148, font, fontSize, black, charSpacingName);
    drawTextOnPage(firstPage, formData.middleInitial?.toUpperCase(), 276, 148, font, fontSize, black, charSpacingName);
    drawTextOnPage(firstPage, formData.lastName?.toUpperCase(), 310, 148, font, fontSize, black, charSpacingName);
    drawTextOnPage(firstPage, formData.address?.toUpperCase(), 61, 123, font, fontSize, black, charSpacingAddress);
    drawTextOnPage(firstPage, formData.city?.toUpperCase(), 60, 100, font, fontSize, black, charSpacingAddress);
    drawTextOnPage(firstPage, formData.state, 298, 100, font, fontSize, black, charSpacingDefault);
    drawTextOnPage(firstPage, formData.zipCode, 342, 100, font, fontSize, black, charSpacingZip);
    drawTextOnPage(firstPage, formData.country?.toUpperCase(), 418, 100, font, fontSize, black, charSpacingDefault);

    let employeeSignature: any = formData.employeeSignature || '';
    if (employeeSignature?.type === 'Buffer') {
      employeeSignature = bufferToBase64(employeeSignature);
    }

    if (employeeSignature?.startsWith('data:image/png;base64,')) {
      try {
        const pngImage = await pdfDoc.embedPng(employeeSignature);
        const sigBoxX = 60;
        const sigBoxY = 62;
        const sigBoxHeight = 35;
        const maxWidth = 180;
        const aspectRatio = pngImage.width / pngImage.height;
        let drawWidth = sigBoxHeight * aspectRatio;
        let drawHeight = sigBoxHeight;
        if (drawWidth > maxWidth) {
          drawWidth = maxWidth;
          drawHeight = maxWidth / aspectRatio;
        }
        const actualSigX = sigBoxX + (maxWidth - drawWidth) / 2;
        firstPage.drawImage(pngImage, {
          x: actualSigX,
          y: sigBoxY + (sigBoxHeight - drawHeight) / 2,
          width: drawWidth,
          height: drawHeight,
        });
      } catch (sigError) {
        console.error('Error embedding signature image:', sigError);
      }
    } else if (formData.employeeSignature) {
      drawTextOnPage(firstPage, formData.employeeSignature, 130, 75, font, fontSize, black, 1); // Adjusted X for typed signature
    }
    if (formData.signatureDate) {
      const sigDateFormatted = moment(formData.signatureDate).isValid()
        ? moment(formData.signatureDate).format('MM/DD/YYYY')
        : formData.signatureDate;
      drawTextOnPage(firstPage, sigDateFormatted, 448, 75, font, fontSize, black, 1.5);
    }

    // --- PAGE 2 ---
    // Single
    if (formData.single_itemizedDeductionsExceed === 'yes')
      drawCheckboxMarkOnPage(secondPage, 437, 646, font, 10, black);
    else if (formData.single_itemizedDeductionsExceed === 'no')
      drawCheckboxMarkOnPage(secondPage, 496, 646, font, 10, black);
    if (formData.single_childDeductionExceed === 'yes') drawCheckboxMarkOnPage(secondPage, 437, 635, font, 10, black);
    else if (formData.single_childDeductionExceed === 'no')
      drawCheckboxMarkOnPage(secondPage, 496, 635, font, 10, black);
    if (formData.single_federalAdjustments === 'yes') drawCheckboxMarkOnPage(secondPage, 437, 625, font, 10, black);
    else if (formData.single_federalAdjustments === 'no') drawCheckboxMarkOnPage(secondPage, 496, 625, font, 10, black);
    if (formData.single_claimTaxCredits === 'yes') drawCheckboxMarkOnPage(secondPage, 437, 615, font, 10, black);
    else if (formData.single_claimTaxCredits === 'no') drawCheckboxMarkOnPage(secondPage, 496, 615, font, 10, black);

    // Married Filing Jointly
    if (formData.joint_itemizedDeductionsExceed === 'yes')
      drawCheckboxMarkOnPage(secondPage, 437, 525, font, 10, black);
    else if (formData.joint_itemizedDeductionsExceed === 'no')
      drawCheckboxMarkOnPage(secondPage, 496, 525, font, 10, black);
    if (formData.joint_childDeductionExceed === 'yes') drawCheckboxMarkOnPage(secondPage, 437, 515, font, 10, black);
    else if (formData.joint_childDeductionExceed === 'no')
      drawCheckboxMarkOnPage(secondPage, 496, 515, font, 10, black);
    if (formData.joint_federalAdjustments === 'yes') drawCheckboxMarkOnPage(secondPage, 437, 505, font, 10, black);
    else if (formData.joint_federalAdjustments === 'no') drawCheckboxMarkOnPage(secondPage, 496, 505, font, 10, black);
    if (formData.joint_claimTaxCredits === 'yes') drawCheckboxMarkOnPage(secondPage, 437, 495, font, 10, black);
    else if (formData.joint_claimTaxCredits === 'no') drawCheckboxMarkOnPage(secondPage, 496, 495, font, 10, black);
    if (formData.joint_spouseBenefits === 'yes') drawCheckboxMarkOnPage(secondPage, 437, 476, font, 10, black);
    else if (formData.joint_spouseBenefits === 'no') drawCheckboxMarkOnPage(secondPage, 496, 476, font, 10, black);

    // Married Filing Separately
    if (formData.separate_itemizedDeductionsExceed === 'yes')
      drawCheckboxMarkOnPage(secondPage, 437, 387, font, 10, black);
    else if (formData.separate_itemizedDeductionsExceed === 'no')
      drawCheckboxMarkOnPage(secondPage, 496, 387, font, 10, black);
    if (formData.separate_childDeductionExceed === 'yes') drawCheckboxMarkOnPage(secondPage, 437, 377, font, 10, black);
    else if (formData.separate_childDeductionExceed === 'no')
      drawCheckboxMarkOnPage(secondPage, 496, 377, font, 10, black);
    if (formData.separate_federalAdjustments === 'yes') drawCheckboxMarkOnPage(secondPage, 437, 367, font, 10, black);
    else if (formData.separate_federalAdjustments === 'no')
      drawCheckboxMarkOnPage(secondPage, 496, 367, font, 10, black);
    if (formData.separate_claimTaxCredits === 'yes') drawCheckboxMarkOnPage(secondPage, 437, 357, font, 10, black);
    else if (formData.separate_claimTaxCredits === 'no') drawCheckboxMarkOnPage(secondPage, 496, 357, font, 10, black);

    if (formData.hoh_itemizedDeductionsExceed === 'yes') drawCheckboxMarkOnPage(secondPage, 436, 268, font, 10, black);
    else if (formData.hoh_itemizedDeductionsExceed === 'no')
      drawCheckboxMarkOnPage(secondPage, 495, 268, font, 10, black);
    if (formData.hoh_childDeductionExceed === 'yes') drawCheckboxMarkOnPage(secondPage, 436, 258, font, 10, black);
    else if (formData.hoh_childDeductionExceed === 'no') drawCheckboxMarkOnPage(secondPage, 495, 258, font, 10, black);
    if (formData.hoh_federalAdjustments === 'yes') drawCheckboxMarkOnPage(secondPage, 436, 248, font, 10, black);
    else if (formData.hoh_federalAdjustments === 'no') drawCheckboxMarkOnPage(secondPage, 495, 248, font, 10, black);
    if (formData.hoh_claimTaxCredits === 'yes') drawCheckboxMarkOnPage(secondPage, 436, 238, font, 10, black);
    else if (formData.hoh_claimTaxCredits === 'no') drawCheckboxMarkOnPage(secondPage, 495, 238, font, 10, black);

    // --- PAGE 3 ---
    // Surviving Spouse
    if (formData.surviving_itemizedDeductionsExceed === 'yes')
      drawCheckboxMarkOnPage(thirdPage, 438, 679, font, 10, black);
    else if (formData.surviving_itemizedDeductionsExceed === 'no')
      drawCheckboxMarkOnPage(thirdPage, 497, 679, font, 10, black);
    if (formData.surviving_childDeductionExceed === 'yes') drawCheckboxMarkOnPage(thirdPage, 438, 669, font, 10, black);
    else if (formData.surviving_childDeductionExceed === 'no')
      drawCheckboxMarkOnPage(thirdPage, 497, 669, font, 10, black);
    if (formData.surviving_federalAdjustments === 'yes') drawCheckboxMarkOnPage(thirdPage, 438, 659, font, 10, black);
    else if (formData.surviving_federalAdjustments === 'no')
      drawCheckboxMarkOnPage(thirdPage, 497, 659, font, 10, black);
    if (formData.surviving_claimTaxCredits === 'yes') drawCheckboxMarkOnPage(thirdPage, 438, 649, font, 10, black);
    else if (formData.surviving_claimTaxCredits === 'no') drawCheckboxMarkOnPage(thirdPage, 497, 649, font, 10, black);

    // Part II Lines
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line1_itemizedDeductions,
      538,
      570,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line2_standardDeduction,
      538,
      526,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line3_subtractLine2FromLine1,
      538,
      510,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line4_childDeductionAmount,
      538,
      495,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line5_federalAdjustmentsToIncome,
      538,
      470,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line6_addLines345,
      538,
      453,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line7_nonwageIncome,
      432,
      436,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line8_ncAdditionsToAgi,
      432,
      411,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line9_addLines78,
      538,
      395,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line10_subtractLine9FromLine6,
      538,
      379,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line11_divideLine10By2500,
      538,
      362,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line12_estimatedNcTaxCredits,
      432,
      338,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line13_divideLine12By109,
      538,
      319,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line14_additionalAllowances,
      538,
      153,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line15_totalAllowancesPartII,
      538,
      138,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line16_spouseAllowancesToClaim,
      538,
      105,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      thirdPage,
      formData.part2_line17_finalTotalAllowances,
      538,
      80,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );

    // --- PAGE 4 ---
    // Schedule 1
    drawRightAlignedTextOnPage(
      fourthPage,
      formData.sch1_homeMortgageInterest,
      412,
      652,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      fourthPage,
      formData.sch1_realEstatePropertyTaxes,
      412,
      642,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      fourthPage,
      formData.sch1_charitableContributions,
      515,
      632,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      fourthPage,
      formData.sch1_medicalAndDentalExpenses,
      515,
      622,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      fourthPage,
      formData.sch1_repaymentOfClaimOfRightIncome,
      515,
      612,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );
    drawRightAlignedTextOnPage(
      fourthPage,
      formData.sch1_totalEstimatedItemizedDeductions,
      515,
      602,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );

    drawRightAlignedTextOnPage(
      fourthPage,
      formData.sch2_estimatedChildDeductionAmount,
      515,
      592,
      font,
      fontSize,
      black,
      charSpacingDefault,
    );

    if (formData?.sch2_single_income_0_20000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_0_20000?.numChildren,
        335,
        408,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_0_20000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_0_20000?.estimatedDeduction,
        500,
        408,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_20001_30000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_20001_30000?.numChildren,
        335,
        398,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_20001_30000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_20001_30000?.estimatedDeduction,
        500,
        398,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_30001_40000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_30001_40000?.numChildren,
        335,
        388,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_30001_40000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_30001_40000?.estimatedDeduction,
        500,
        388,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_40001_50000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_40001_50000?.numChildren,
        335,
        378,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_40001_50000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_40001_50000?.estimatedDeduction,
        500,
        378,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_50001_60000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_50001_60000?.numChildren,
        335,
        368,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_50001_60000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_50001_60000?.estimatedDeduction,
        500,
        368,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_60001_70000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_60001_70000?.numChildren,
        335,
        358,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_60001_70000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_60001_70000?.estimatedDeduction,
        500,
        358,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_over_70000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_over_70000?.numChildren,
        335,
        348,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_single_income_over_70000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_single_income_over_70000?.estimatedDeduction,
        500,
        348,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_0_40000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_0_40000?.numChildren,
        335,
        328,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_0_40000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_0_40000?.estimatedDeduction,
        500,
        328,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_40001_60000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_40001_60000?.numChildren,
        335,
        318,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_40001_60000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_40001_60000?.estimatedDeduction,
        500,
        318,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_60001_80000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_60001_80000?.numChildren,
        335,
        308,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_60001_80000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_60001_80000?.estimatedDeduction,
        500,
        308,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_80001_100000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_80001_100000?.numChildren,
        335,
        298,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_80001_100000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_80001_100000?.estimatedDeduction,
        500,
        298,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_100001_120000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_100001_120000?.numChildren,
        335,
        288,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_100001_120000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_100001_120000?.estimatedDeduction,
        500,
        288,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_120001_140000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_120001_140000?.numChildren,
        335,
        278,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_120001_140000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_120001_140000?.estimatedDeduction,
        500,
        278,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_over_140000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_over_140000?.numChildren,
        335,
        268,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfj_ss_income_over_140000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfj_ss_income_over_140000?.estimatedDeduction,
        500,
        268,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_0_30000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_0_30000?.numChildren,
        335,
        248,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_0_30000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_0_30000?.estimatedDeduction,
        500,
        248,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_30001_45000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_30001_45000?.numChildren,
        335,
        238,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_30001_45000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_30001_45000?.estimatedDeduction,
        500,
        238,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_45001_60000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_45001_60000?.numChildren,
        335,
        228,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_45001_60000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_45001_60000?.estimatedDeduction,
        500,
        228,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_60001_75000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_60001_75000?.numChildren,
        335,
        218,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_60001_75000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_60001_75000?.estimatedDeduction,
        500,
        218,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_75001_90000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_75001_90000?.numChildren,
        335,
        208,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_75001_90000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_75001_90000?.estimatedDeduction,
        500,
        208,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_90001_105000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_90001_105000?.numChildren,
        335,
        198,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_90001_105000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_90001_105000?.estimatedDeduction,
        500,
        198,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_over_105000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_over_105000?.numChildren,
        335,
        188,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_hoh_income_over_105000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_hoh_income_over_105000?.estimatedDeduction,
        500,
        188,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_0_20000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_0_20000?.numChildren,
        335,
        169,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_0_20000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_0_20000?.estimatedDeduction,
        500,
        169,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_20001_30000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_20001_30000?.numChildren,
        335,
        159,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_20001_30000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_20001_30000?.estimatedDeduction,
        500,
        159,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_30001_40000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_30001_40000?.numChildren,
        335,
        149,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_30001_40000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_30001_40000?.estimatedDeduction,
        500,
        149,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_40001_50000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_40001_50000?.numChildren,
        335,
        139,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_40001_50000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_40001_50000?.estimatedDeduction,
        500,
        139,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_50001_60000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_50001_60000?.numChildren,
        335,
        130,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_50001_60000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_50001_60000?.estimatedDeduction,
        500,
        130,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_60001_70000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_60001_70000?.numChildren,
        335,
        120,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_60001_70000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_60001_70000?.estimatedDeduction,
        500,
        120,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_over_70000?.numChildren)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_over_70000?.numChildren,
        335,
        110,
        font,
        8,
        black,
        charSpacingDefault,
      );

    if (formData?.sch2_mfs_income_over_70000?.estimatedDeduction)
      drawRightAlignedTextOnPage(
        fourthPage,
        formData?.sch2_mfs_income_over_70000?.estimatedDeduction,
        500,
        110,
        font,
        8,
        black,
        charSpacingDefault,
      );

    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
  } catch (error) {
    console.error('Error filling NC-4 PDF:', error);
    return null;
  }
}
